import { useCallback, useState } from "react";

import { useHistory } from "react-router";
import { Box, Typography } from "@mui/material";

import { cloudflowTexts } from "../../../assets/texts";
import { FilterTable } from "../../../Components/FilterTable/FilterTable";
import { FilterTableSkeleton } from "../../../Components/FilterTable/FilterTableSkeleton";
import LoadingButton from "../../../Components/LoadingButton";
import { useCustomerContext } from "../../../Context/CustomerContext";
import { isCustomerInPresentationMode } from "../../../Context/useCustomerOrPresentationModeCustomer";
import { type CloudflowWSnap } from "../../../types/Cloudflow";
import DeleteCloudFlowDialog from "../Dialog/DeleteCloudFlow";
import CloudflowRow from "./CloudflowRow";
import { filters, headerColumns } from "./Columns";

type CloudflowBrowserProps = {
  cloudflows?: CloudflowWSnap[];
  loading: boolean;
  createCloudFlowLoading: boolean;
  onCreateCloudflow: () => void;
};

const CloudflowBrowser = ({
  cloudflows,
  loading,
  createCloudFlowLoading,
  onCreateCloudflow,
}: CloudflowBrowserProps) => {
  const history = useHistory();
  const { customer } = useCustomerContext();
  const isPresentationMode = isCustomerInPresentationMode(customer);
  const [deleteDialogId, setDeleteDialogId] = useState<string | null>(null);

  const onViewRunHistory = useCallback(
    (row?: CloudflowWSnap) => {
      const pathname = `/customers/${customer.id}/cloudflow/history`;
      const state = row && !isPresentationMode ? { name: row.data.name } : undefined;

      history.push({
        pathname,
        state,
      });
    },
    [customer.id, history, isPresentationMode]
  );

  const onDelete = useCallback((id: string) => {
    setDeleteDialogId(id);
  }, []);

  const handelCloseDeleteDialog = useCallback(() => {
    setDeleteDialogId(null);
  }, []);

  const CloudflowRowWrapper = useCallback(
    ({ row }: { row: CloudflowWSnap }) => (
      <CloudflowRow
        row={row}
        actions={{
          onViewRunHistory,
          onDelete,
        }}
      />
    ),
    [onDelete, onViewRunHistory]
  );

  return loading ? (
    <Box
      sx={{
        p: 1,
      }}
    >
      <FilterTableSkeleton />
    </Box>
  ) : (
    <>
      <FilterTable<CloudflowWSnap>
        tableItems={cloudflows}
        rowComponent={CloudflowRowWrapper}
        headerColumns={headerColumns}
        filterColumns={filters}
        filterBarPlaceholder={cloudflowTexts.FILTER_TABLE}
        persistenceKey="cloudflows_v1"
        emptyTableComponent={<Box data-cy="noCloudFlowsFound">{cloudflowTexts.NO_CLOUDFLOWS_FOUND}</Box>}
        defaultSortDirection="desc"
        itemUniqIdentifierField="ref.id"
        defaultSortingColumnValue="data.lastExecuted"
        toolbarProps={{
          title: (
            <Typography variant="h4" data-cy="allCloudflowsSubTitle">
              {cloudflowTexts.YOUR_CLOUDFLOWS}
            </Typography>
          ),
          secondaryButtons: [
            {
              text: cloudflowTexts.VIEW_RUN_HISTORY,
              onClick: () => {
                onViewRunHistory();
              },
              "data-cy": "viewRunHistoryToolbar",
            },
          ],
          customSlot: (
            <LoadingButton
              loading={createCloudFlowLoading}
              onClick={onCreateCloudflow}
              data-cy="createCloudflow"
              variant="contained"
              mixpanelEventId="cloudflow.create"
              disabled={isPresentationMode}
            >
              {cloudflowTexts.CREATE_CLOUDFLOW}
            </LoadingButton>
          ),
        }}
      />
      {Boolean(deleteDialogId) && (
        <DeleteCloudFlowDialog flowId={deleteDialogId} handleClose={handelCloseDeleteDialog} />
      )}
    </>
  );
};

export default CloudflowBrowser;
